import { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LocateFixed, Radio, Users } from "lucide-react";
import { api, errMsg, money, fdate } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { useUserLocation, distM } from "@/lib/location";
import { PageHeader, LiveIndicator, Loading, DemoBadge } from "@/components/common";
import { GeoMap } from "@/components/GeoMap";

export default function Collections() {
  const debts = useFetch("/intranet/debts", { poll: 20000 });
  const crews = useFetch("/intranet/crews", { poll: 5000 });
  const [showHeat, setShowHeat] = useState(true);
  const [showCrews, setShowCrews] = useState(true);
  const [live, setLive] = useState(false);
  const [manual, setManual] = useState(false);
  const loc = useUserLocation();
  const lastSent = useRef({ p: null, t: 0 });

  const send = async (lat, lng, quiet = false) => {
    try {
      await api.post("/intranet/crews/me/location", { lat, lng });
      lastSent.current = { p: [lat, lng], t: Date.now() };
      if (!quiet) toast.success("Ubicación compartida con el centro de mando");
      crews.reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  // Rastreo en vivo: envía la posición GPS exacta al moverse >20 m o cada 15 s
  useEffect(() => {
    if (!live || !loc.position) return;
    const { p, t } = lastSent.current;
    if (!p || distM(p, loc.position) > 20 || Date.now() - t > 15000) send(loc.position[0], loc.position[1], !!p);
  }, [live, loc.coords?.lat, loc.coords?.lng, loc.coords?.at]); // eslint-disable-line
  useEffect(() => {
    if (!live) return undefined;
    const iv = setInterval(() => { if (loc.position) send(loc.position[0], loc.position[1], true); }, 15000);
    return () => clearInterval(iv);
  }, [live, loc.position]); // eslint-disable-line

  const toggleLive = (on) => {
    if (on && !loc.position) {
      loc.request();
      if (loc.status === "denied" || loc.status === "unsupported") { setManual(true); toast.info("GPS bloqueado: toca el mapa para marcar tu posición"); return; }
    }
    setLive(on);
    if (on) toast.success("Rastreo en vivo activado");
  };
  const share = () => {
    if (loc.position) return send(loc.position[0], loc.position[1]);
    loc.request();
    setManual(true);
    toast.info("Esperando GPS… o toca el mapa para marcar tu posición");
  };

  return (
    <div>
      <PageHeader eyebrow="Cobranza en campo" title="Mapa de calor de adeudos y cuadrillas" subtitle="Concentración de adeudos (predial, agua, licencias y multas) y posición en vivo de las cuadrillas."
        actions={<>
          <LiveIndicator lastSync={crews.lastSync} />
          <div className="flex items-center gap-2 rounded-md border px-3 py-1.5 bg-background">
            <Radio className={`h-4 w-4 ${live ? "text-emerald-600" : "text-muted-foreground"}`} />
            <Label htmlFor="live-track" className="text-sm">Rastreo en vivo</Label>
            <Switch id="live-track" checked={live} onCheckedChange={toggleLive} data-testid="toggle-live-tracking" />
          </div>
          <Button variant="outline" onClick={share} data-testid="share-location-button"><LocateFixed className="h-4 w-4 mr-1" />Compartir mi ubicación</Button>
        </>} />
      <div className="grid gap-4 lg:grid-cols-[1.8fr_1fr]">
        <Card>
          <CardHeader className="pb-2 flex flex-row flex-wrap items-center gap-4 space-y-0">
            <CardTitle className="text-base mr-auto">Quintana Roo</CardTitle>
            <div className="flex items-center gap-2"><Switch id="h" checked={showHeat} onCheckedChange={setShowHeat} data-testid="toggle-heatmap" /><Label htmlFor="h" className="text-sm">Adeudos</Label></div>
            <div className="flex items-center gap-2"><Switch id="c" checked={showCrews} onCheckedChange={setShowCrews} data-testid="toggle-crews" /><Label htmlFor="c" className="text-sm">Cuadrillas</Label></div>
          </CardHeader>
          <CardContent>
            {manual && <div className="mb-2 text-xs rounded-md bg-amber-50 border border-amber-200 px-3 py-2">Toca el mapa para registrar tu posición actual.</div>}
            <GeoMap className="h-[560px]" testid="collections-map" title="Cobranza · cuadrillas en ruta" zoom={7} pitch={50}
              debts={showHeat ? (debts.data?.points || []) : null} crews={showCrews ? (crews.data || []) : null}
              onPick={manual ? ([lat, lng]) => { setManual(false); send(lat, lng); } : null} />
            <div className="flex flex-wrap gap-3 mt-3 text-xs text-muted-foreground">
              {[["#5FD3E0", "< $5k"], ["#F2C14E", "$5k–10k"], ["#FF8A50", "$10k–18k"], ["#FF5A6A", "> $18k"]].map(([c, l]) => <span key={l} className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full" style={{ background: c }} />{l}</span>)}
              <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-[#2EE6C8]" />Cuadrilla (estela animada)</span>
            </div>
          </CardContent>
        </Card>
        <div className="space-y-4">
          <Card><CardHeader className="pb-2"><CardTitle className="text-base">Adeudo total</CardTitle></CardHeader>
            <CardContent>
              {!debts.data ? <Loading rows={2} /> : (
                <>
                  <div className="text-3xl font-semibold font-display" data-testid="debts-total">{money(debts.data.total)}</div>
                  <div className="text-xs text-muted-foreground mb-3">{debts.data.points.length} cuentas con adeudo</div>
                  <div className="space-y-2">{debts.data.by_zone.map((z) => (
                    <div key={z.zone}>
                      <div className="flex justify-between text-sm"><span>{z.zone}</span><span className="font-mono">{money(z.amount)}</span></div>
                      <div className="h-1.5 rounded-full bg-secondary mt-1"><div className="h-1.5 rounded-full bg-[#C77700]" style={{ width: `${(z.amount / debts.data.by_zone[0].amount) * 100}%` }} /></div>
                    </div>
                  ))}</div>
                </>
              )}
            </CardContent>
          </Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" />Cuadrillas activas</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {(crews.data || []).map((c) => (
                <div key={c.crew_id} className="rounded-lg border p-3" data-testid={`crew-${c.crew_id}`}>
                  <div className="flex items-center justify-between"><span className="text-sm font-medium">{c.name}</span>{c.is_demo && <DemoBadge />}</div>
                  <div className="text-xs text-muted-foreground font-mono">{c.lat.toFixed(4)}, {c.lng.toFixed(4)} · {fdate(c.last_update)}</div>
                  {c.collected_today > 0 && <div className="text-xs mt-1">Cobrado hoy: <span className="font-mono">{money(c.collected_today)}</span></div>}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
